import { useMutation, useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import {
  closeProperty,
  CloseListingPayload,
  deleteProperty,
  getMyDashboardAnalytics,
  getMyListingsReport,
  getMydashboardProperties,
  updateProperty,
} from "../api/dashboard/dashboardApi";
import { queryClient } from "../main";
import { PropertyRequest } from "../types/listings/listing.model";
import { toast } from "sonner";

// staleTime + refetchOnWindowFocus:false — React Query's default (staleTime 0,
// refetch on every focus) meant just alt-tabbing back to the dashboard, or any
// remount (e.g. a logout/login cycle), re-hit this endpoint from scratch.
// That's suspected to be why "Всего просмотров" was seen jumping between one
// visit and the next with no real traffic in between — the dashboard/property
// endpoints appear to bump their own view counters just by being read. This
// can't be fixed from the frontend (it's server-side), but not re-fetching
// on every tab-focus/remount at least stops the app itself from triggering it
// more than once a minute.
const DASHBOARD_STALE_TIME = 60_000;

export const useGetMyDashboardPropertiesWithFilters = (
  offset: number,
  page: number,
) => {
  return useQuery({
    queryKey: ["dashboardProperties", offset, page],
    queryFn: () => getMydashboardProperties(offset, page),
    staleTime: DASHBOARD_STALE_TIME,
    refetchOnWindowFocus: false,
  });
};

export const useGetMyDashboardAnalytics = () => {
  return useQuery({
    queryKey: ["dashboardAnalytics"],
    queryFn: getMyDashboardAnalytics,
    staleTime: DASHBOARD_STALE_TIME,
    refetchOnWindowFocus: false,
  });
};

export const useGetMyListingsReport = (enabled = true) => {
  return useQuery({
    queryKey: ["dashboardReport"],
    queryFn: getMyListingsReport,
    enabled,
    staleTime: DASHBOARD_STALE_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useUpdateProperty = (setEditOpen: () => void) => {
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PropertyRequest }) =>
      updateProperty(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboardProperties"] });
      setEditOpen();
    },
  });
};

export const useDeleteProperty = (setDeleteOpen: () => void) => {
  return useMutation({
    mutationFn: (id: number) => deleteProperty(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboardProperties"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardAnalytics"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardReport"] });
      setDeleteOpen();
      toast.success(t("dashboard.deleteSuccess"));
    },
    onError: () => {
      setDeleteOpen();
      toast.error(t("dashboard.deleteError"));
    },
  });
};

/** Снять объявление с причиной: «Продано» (остаётся в профиле) или удалить. */
export const useCloseProperty = (onDone: () => void) => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CloseListingPayload }) =>
      closeProperty(id, payload),
    onSuccess: ({ deleted }) => {
      queryClient.invalidateQueries({ queryKey: ["dashboardProperties"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardAnalytics"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardReport"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      onDone();
      toast.success(
        deleted
          ? t("dashboard.deleteSuccess")
          : t("closeListing.soldSuccess", "Отмечено как продано. Поздравляем!"),
      );
    },
    onError: () => {
      onDone();
      toast.error(t("dashboard.deleteError"));
    },
  });
};
